import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const MembershipTierCodeSchema = z.enum(['SILVER', 'GOLD', 'PLATINUM']);
export type MembershipTierCode = z.infer<typeof MembershipTierCodeSchema>;

export const BillingCycleSchema = z.enum(['ANNUAL_UPFRONT', 'MONTHLY_INSTALLMENT']);
export type BillingCycle = z.infer<typeof BillingCycleSchema>;

export const MembershipStatusSchema = z.enum([
  'PENDING_PAYMENT',
  'ACTIVE',
  'SUSPENDED',
  'EXPIRED',
  'CANCELLED',
]);
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const EnrollMemberSchema = z.object({
  customerId: z.string().min(1),
  customerName: z.string().min(1),
  customerEmail: z.string().email(),
  tierCode: MembershipTierCodeSchema,
  billingCycle: BillingCycleSchema,
  startDate: z.string().min(1),
  autoRenewEnabled: z.boolean().default(true),
  notes: z.string().max(500).optional(),
});
export type EnrollMemberInput = z.infer<typeof EnrollMemberSchema>;

export const UpdateTierSchema = z.object({
  name: z.string().min(1).optional(),
  nameId: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  descriptionId: z.string().nullable().optional(),
  priceMultiplier: z.number().positive().optional(),
  validityDays: z.number().int().positive().optional(),
  parkingIncluded: z.boolean().optional(),
  parkingType: z.string().nullable().optional(),
  guestPassesPerVisit: z.number().int().min(0).optional(),
  guestDiscountPercent: z.number().min(0).max(100).optional(),
  fnbDiscountPercent: z.number().min(0).max(100).optional(),
  merchDiscountPercent: z.number().min(0).max(100).optional(),
  earlyAccessMinutes: z.number().int().min(0).optional(),
  earlyAccessDays: z.array(z.string()).optional(),
  specialEventsPerYear: z.number().int().min(0).optional(),
  hasUnlimitedReentry: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type UpdateTierInput = z.infer<typeof UpdateTierSchema>;

export const RenewMemberSchema = z.object({
  tierCode: MembershipTierCodeSchema.optional(),
  billingCycle: BillingCycleSchema.optional(),
  autoRenewEnabled: z.boolean().optional(),
});
export type RenewMemberInput = z.infer<typeof RenewMemberSchema>;

export const UpgradeMemberSchema = z.object({
  targetTierCode: MembershipTierCodeSchema,
});
export type UpgradeMemberInput = z.infer<typeof UpgradeMemberSchema>;

/**
 * Recording a payment that was actually collected against a membership.
 *
 * Enrolment and renewal used to write `paidIdr = totalPriceIdr` and set the membership
 * ACTIVE without any money moving, so the only record that a member had paid was a
 * number the system wrote about itself. This is the input that makes the claim
 * traceable to something outside the system.
 *
 * `reference` is required and not optional on purpose — it is the whole point. A
 * payment with no receipt number, gateway id or EDC slip reference is indistinguishable
 * from the fabrication this replaces.
 */
export const RecordMembershipPaymentSchema = z.object({
  amountIdr: z.number().int().positive(),
  method: z.enum(['CASH', 'CARD', 'BANK_TRANSFER', 'EDC', 'QRIS', 'EWALLET', 'OTHER']),
  reference: z.string().min(1).max(200),
  /** When the money was taken, if that is not now. ISO 8601. */
  paidAt: z.string().datetime().optional(),
  notes: z.string().max(500).optional(),
});
export type RecordMembershipPaymentInput = z.infer<typeof RecordMembershipPaymentSchema>;

export const CancelMemberSchema = z.object({
  reason: z.string().max(1000).optional(),
  processRefund: z.boolean().optional(),
});
export type CancelMemberInput = z.infer<typeof CancelMemberSchema>;

const BlackoutDateEntrySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().max(200).optional(),
});

export const SetBlackoutDatesSchema = z.object({
  year: z.number().int().min(2024).max(2099),
  dates: z.array(BlackoutDateEntrySchema),
});
export type SetBlackoutDatesInput = z.infer<typeof SetBlackoutDatesSchema>;

export const MembershipAnalyticsQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type MembershipAnalyticsQuery = z.infer<typeof MembershipAnalyticsQuerySchema>;

export const ListMembersQuerySchema = z.object({
  page: z.string().optional(),
  limit: z.string().optional(),
  tier: z.string().optional(),
  status: z.string().optional(),
  search: z.string().optional(),
  expiringWithinDays: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type ListMembersQuery = z.infer<typeof ListMembersQuerySchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const SerializedTierSchema = z.object({
  id: z.string().uuid(),
  tierCode: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  priceMultiplier: z.number(),
  validityDays: z.number().int(),
  parkingIncluded: z.boolean(),
  parkingType: z.string().nullable(),
  guestPassesPerVisit: z.number().int(),
  guestDiscountPercent: z.number(),
  fnbDiscountPercent: z.number(),
  merchDiscountPercent: z.number(),
  earlyAccessMinutes: z.number().int(),
  earlyAccessDays: z.array(z.string()),
  specialEventsPerYear: z.number().int(),
  hasUnlimitedReentry: z.boolean(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  metadata: z.unknown(),
  activeMemberCount: z.number().int().optional(),
  blackoutDateCount: z.number().int().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type SerializedTier = z.infer<typeof SerializedTierSchema>;

export const BlackoutDateResponseSchema = z.object({
  id: z.string().uuid(),
  blackoutDate: z.string(),
  reason: z.string().nullable(),
});
export type BlackoutDateResponse = z.infer<typeof BlackoutDateResponseSchema>;

export const TierDetailResponseSchema = SerializedTierSchema.extend({
  blackoutDates: z.array(BlackoutDateResponseSchema),
});
export type TierDetailResponse = z.infer<typeof TierDetailResponseSchema>;

export const SerializedMembershipSchema = z.object({
  id: z.string().uuid(),
  membershipNumber: z.string(),
  customerId: z.string(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  tierId: z.string().uuid(),
  tierCode: z.string().optional(),
  tierName: z.string().optional(),
  tierNameId: z.string().optional(),
  status: z.string(),
  billingCycle: z.string(),
  enrolledAt: z.string().nullable(),
  activatedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  renewedAt: z.string().nullable(),
  previousMembershipId: z.string().nullable(),
  totalPriceIdr: z.number(),
  paidIdr: z.number(),
  orderId: z.string().nullable(),
  autoRenewEnabled: z.boolean(),
  transferCount: z.number().int(),
  maxTransfers: z.number().int(),
  photoUrl: z.string().nullable(),
  photoVerifiedAt: z.string().nullable(),
  photoVerifiedBy: z.string().nullable(),
  metadata: z.unknown(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type SerializedMembership = z.infer<typeof SerializedMembershipSchema>;

export const MemberDetailResponseSchema = SerializedMembershipSchema.extend({
  tier: SerializedTierSchema,
  billingSchedule: z.array(z.object({
    id: z.string().uuid(),
    installmentNo: z.number().int(),
    dueDate: z.string(),
    amountIdr: z.number(),
    status: z.string(),
    paymentId: z.string().nullable(),
    paidAt: z.string().nullable(),
    failureReason: z.string().nullable(),
    retryCount: z.number().int(),
  })),
  guestPasses: z.array(z.object({
    id: z.string().uuid(),
    visitDate: z.string(),
    guestName: z.string(),
    ticketId: z.string().nullable(),
    discountPercent: z.number(),
    pricePaidIdr: z.number(),
    issuedAt: z.string(),
    issuedBy: z.string().nullable(),
  })),
});
export type MemberDetailResponse = z.infer<typeof MemberDetailResponseSchema>;

export const MembershipAnalyticsResponseSchema = z.object({
  summary: z.object({
    totalActiveMembers: z.number().int(),
    byTier: z.record(z.number().int()),
    newEnrollmentsThisPeriod: z.number().int(),
    cancellationsThisPeriod: z.number().int(),
    renewalsThisPeriod: z.number().int(),
    churnRate: z.number(),
    totalRevenueIdr: z.number(),
    expiringIn30Days: z.number().int(),
  }),
  enrollmentTrend: z.array(z.object({
    month: z.string(),
    enrollments: z.number().int(),
    cancellations: z.number().int(),
  })),
  revenueByTier: z.array(z.object({
    tier: z.string(),
    revenueIdr: z.number(),
    memberCount: z.number().int(),
  })),
});
export type MembershipAnalyticsResponse = z.infer<typeof MembershipAnalyticsResponseSchema>;

export const PaginatedMembersResponseSchema = z.object({
  members: z.array(SerializedMembershipSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

// ============================================================================
// Admin membership compat surface — ticketing-service `/admin/membership/*`
// ============================================================================
//
// Both endpoints accept one of two spellings for the same identifier, because
// the monolith's admin client sends `tierCode`/`memberId` while this service's
// own surfaces use `tierId`/`membershipId`. The handlers coalesce them with
// `String(a ?? b ?? '')` — and that empty-string fallback is the defect: it
// turned a missing id into a lookup for the record whose id is `''`, which
// Prisma rejects, so the caller got a 500 instead of "you forgot the id".
//
// The `.refine` is what the coalesce should always have been: at least one of
// the pair must be present, checked before anything reaches the database.

export const AddMembershipBlackoutSchema = z
  .object({
    tierId: z.string().min(1).optional(),
    tierCode: z.string().min(1).optional(),
    // Mirrors the handler's own check, which was already correct and already
    // returned 400. Declared here so the rule lives with the shape.
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
    reason: z.string().optional(),
  })
  .refine((b) => Boolean(b.tierId ?? b.tierCode), {
    message: 'one of tierId or tierCode is required',
    path: ['tierId'],
  });
export type AddMembershipBlackoutInput = z.infer<typeof AddMembershipBlackoutSchema>;

export const EvaluateMembershipSchema = z
  .object({
    membershipId: z.string().min(1).optional(),
    memberId: z.string().min(1).optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD').optional(),
  })
  .refine((b) => Boolean(b.membershipId ?? b.memberId), {
    message: 'one of membershipId or memberId is required',
    path: ['membershipId'],
  });
export type EvaluateMembershipInput = z.infer<typeof EvaluateMembershipSchema>;
